import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  saveFishboneApiKey,
  getFishboneApiKey,
  deleteFishboneApiKey,
} from "../api/settingsApi";
import { toast } from "sonner";

interface FishboneConfigurationProps {
  primaryColor: string;
}

function FishboneConfiguration({ primaryColor }: FishboneConfigurationProps) {
  const [showForm, setShowForm] = useState(false);

  const fishboneKeyQuery = useQuery({
    queryKey: ["/admin/fishbone-key"],
    queryFn: getFishboneApiKey,
    refetchOnWindowFocus: false,
  });

  const saveKeyMutation = useMutation({
    mutationFn: saveFishboneApiKey,
    onSuccess: () => {
      toast.success("Fishbone API key saved successfully");
      fishboneKeyQuery.refetch();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error || "Failed to save Fishbone API key",
      );
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: deleteFishboneApiKey,
    onSuccess: () => {
      toast.success("Fishbone API key removed successfully");
      fishboneKeyQuery.refetch();
    },
    onError: () => {
      toast.error("Failed to remove Fishbone API key");
    },
  });

  const isConfigured = fishboneKeyQuery.data?.configured;
  const isExpired = fishboneKeyQuery.data?.isExpired;
  const expiresAt = fishboneKeyQuery.data?.expiresAt;
  const createdAt = fishboneKeyQuery.data?.createdAt;

  // Calculate days remaining from today
  const getDaysRemaining = () => {
    if (!expiresAt) return 0;
    const expiryDate = new Date(expiresAt);
    const today = new Date();
    // Reset time to midnight for accurate day calculation
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Mask the API key - show only last 4 characters
  const maskApiKey = (key: string) => {
    if (!key || key.length < 4) return "****";
    return "*".repeat(key.length - 4) + key.slice(-4);
  };

  if (showForm) {
    // Calculate default date (90 days from now)
    const getDefaultDate = () => {
      const date = new Date();
      date.setDate(date.getDate() + 90);
      return date.toISOString().split("T")[0];
    };

    return (
      <Formik
        enableReinitialize
        initialValues={{
          apiKey: "",
          expiryDate: getDefaultDate(),
        }}
        validationSchema={Yup.object({
          apiKey: Yup.string()
            .required("API Key is required")
            .matches(/^fyi_sk_/, "API Key must start with 'fyi_sk_'"),
          expiryDate: Yup.date()
            .required("Expiry date is required")
            .min(new Date(), "Expiry date must be in the future"),
        })}
        onSubmit={(
          values: {
            apiKey: string;
            expiryDate: string;
          },
          { setSubmitting }: any,
        ) => {
          // Save Fishbone API Key using POST
          const expiryDate = new Date(values.expiryDate);
          const today = new Date();
          const diffTime = expiryDate.getTime() - today.getTime();
          const expiryDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          saveKeyMutation.mutate({
            apiKey: values.apiKey,
            expiryDays: expiryDays,
          });

          setSubmitting(false);
          setShowForm(false);
        }}
      >
        {({ isSubmitting, handleReset, values, setFieldValue }) => (
          <Form>
            <div className="w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Add Fishbone API Key</h2>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ← Back to List
                </button>
              </div>

              {/* Fishbone API Key Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">Fishbone API Key</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      API Key
                    </label>
                    <Field
                      name="apiKey"
                      type="password"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Enter Fishbone API key (starts with fyi_sk_)"
                    />
                    <ErrorMessage
                      name="apiKey"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Expiry Date
                    </label>
                    <Field
                      name="expiryDate"
                      type="date"
                      className="w-full border rounded px-3 py-2"
                    />
                    <ErrorMessage
                      name="expiryDate"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-4 mt-8">
              <button
                type="submit"
                style={{ backgroundColor: primaryColor }}
                className="text-white px-6 py-2 rounded font-semibold"
                disabled={isSubmitting || saveKeyMutation.isPending}
              >
                {saveKeyMutation.isPending ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                style={{ color: primaryColor, borderColor: primaryColor }}
                className="px-6 py-2 rounded font-semibold border"
                onClick={handleReset}
                disabled={isSubmitting || saveKeyMutation.isPending}
              >
                Reset
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Fishbone Configuration</h2>
        {!isConfigured && (
          <button
            onClick={() => setShowForm(true)}
            style={{ backgroundColor: primaryColor }}
            className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
          >
            + Add Key
          </button>
        )}
      </div>

      {fishboneKeyQuery.isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : !isConfigured ? (
        <div className="text-center py-8 text-gray-500">
          No Fishbone API key configured yet. Click "Add Key" to get started.
        </div>
      ) : (
        <div className="border rounded-lg p-6 bg-white shadow-sm">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-lg"
                style={{ backgroundColor: primaryColor }}
              >
                F
              </div>
              <div>
                <h3 className="font-semibold text-lg">Fishbone API Key</h3>
                <span className="text-xs text-gray-500">
                  {fishboneKeyQuery.data?.name || "Default API Key"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs px-2 py-1 rounded ${
                  isExpired
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {isExpired ? "Expired" : "Active"}
              </span>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center text-sm">
              <span className="text-gray-500 w-32">API Key:</span>
              <span className="text-gray-700 font-mono">
                fyi_sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxx
              </span>
            </div>
            <div className="flex items-center text-sm">
              <span className="text-gray-500 w-32">Expires:</span>
              <span className="text-gray-700">
                {expiresAt ? new Date(expiresAt).toLocaleDateString() : "N/A"}
              </span>
            </div>
            <div className="flex items-center text-sm">
              {/* <span className="text-gray-500 w-32">Created:</span>
              <span className="text-gray-700">
                {fishboneKeyQuery.data?.createdAt
                  ? new Date(
                      fishboneKeyQuery.data.createdAt,
                    ).toLocaleDateString()
                  : "N/A"}
              </span> */}
            </div>
          </div>

          {/* Round progress bar showing days remaining */}
          <div className="mb-6">
            <div className="flex items-center">
              <span className="text-sm font-semibold text-gray-700 ml-auto">
                {getDaysRemaining()} days left
              </span>
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <button
              onClick={() => setShowForm(true)}
              style={{ backgroundColor: primaryColor }}
              className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
            >
              Update Key
            </button>
            {/* <button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to remove the Fishbone API key?",
                  )
                ) {
                  deleteKeyMutation.mutate();
                }
              }}
              style={{ color: "#ef4444", borderColor: "#ef4444" }}
              className="px-4 py-2 rounded font-semibold border hover:bg-red-50 transition-colors"
            >
              Remove Key
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
}

export default FishboneConfiguration;
